import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import WhatsAppNotice from '../../components/common/WhatsAppNotice';
import EventCta from '../../components/common/EventCta';
import { apiRequest, handleBranding, applyStoredBrandingColors } from '../../utils';

/* Public accept page (/admin-invite/:token). Serves BOTH company invites, because they differ
   only in the role they grant and in nothing the visitor does:
     - ADMIN  — the contact person shared the link from "manage members"; accepting makes the
                person an admin and signs them into the contact view.
     - MEMBER — the admin uploaded them and mailed an invite; accepting puts them on the roster
                and signs them into the member view. The address is baked into the link, so the
                field is filled in and read-only, and a mobile number is optional (they are not
                being given access to anything of ours).
   The twin of InviteAccept (per-Lab seat), but company-wide: no seat is spent and there is no
   Lab to greet them with.
   Backend: GET /api/bravos/admin-invite/:token + POST /api/bravos/admin-invite/:token/accept. */
const AdminInviteAccept = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  // Set when the typed email already has an account: the shareable link can't prove it's
  // theirs, so they log in first and reopen the link (then it grants onto that session).
  const [loginFirst, setLoginFirst] = useState(false);
  // Set when this browser is signed in as somebody OTHER than the person the invite was mailed
  // to. Holds their name (or '' when the backend has none) — null means "not the case".
  const [wrongAccount, setWrongAccount] = useState(null);
  // A visitor who is already logged in accepts onto their own account (the backend resolves them
  // from the session cookie), so we skip the details form. This flag can be STALE — the flag
  // outlives the cookie — so a failed accept falls back to the form below.
  const [signedIn, setSignedIn] = useState(!!localStorage.getItem('isLoggedIn'));

  // An invite mailed to a known address carries it; the form shows it and won't let it change,
  // so a forwarded mail can't enrol somebody else against a colleague's row.
  const boundEmail = info && info.email;
  const isAdminInvite = !info || info.role !== 'member';
  // Every brand's company admin can send these since 2026-10-18; only Bravos may say "on Bravos"
  // and talk about pulses and tickets. The other brands get the same sentence naming themselves.
  const isBravos = !info || info.is_bravos !== false;
  const k = (key) => (isBravos ? key : `${key}Brand`);
  const brand = (info && info.community_name) || '';
  // Set when a colleague joined a brand that has no member home: they are on the roster now and
  // the app opens for them once their admin puts them in a team, so the page ends here.
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    if (boundEmail) setEmail(boundEmail);
  }, [boundEmail]);

  useEffect(() => {
    (async () => {
      // Resolve default branding first, THEN override with the invite's own BU brand, so a fresh
      // invitee lands on the Bravos look, not Tornea's. Await handleBranding so it can't clobber
      // our override afterwards.
      await handleBranding();
      try {
        const data = await apiRequest('GET', `/bravos/admin-invite/${token}`);
        setInfo(data);
        if (data && data.valid) {
          if (data.primary_color) localStorage.setItem('primaryColor', data.primary_color);
          if (data.image_link) localStorage.setItem('backgroundPicture', data.image_link);
          applyStoredBrandingColors();
          window.dispatchEvent(new Event('branding-changed'));
        }
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  const accept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await apiRequest('POST', `/bravos/admin-invite/${token}/accept`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
      });
      if (data && data.joined) {
        setJoined(true);
        return;
      }
      localStorage.setItem('isLoggedIn', '1');
      window.dispatchEvent(new Event('auth-changed'));
      navigate(data.redirect || '/member-home');
    } catch (err) {
      if (err.message === 'accountExistsLoginFirst') {
        setLoginFirst(true);
      } else if (err.message === 'signedInAsSomeoneElse') {
        setWrongAccount(err.data?.signed_in_as || '');
      } else if (signedIn && (err.message === 'invalidEmail' || err.message === 'invalidMobile')) {
        // The "logged in" flag was stale (cookie gone): the backend fell through to provisioning
        // and rejected our empty fields. Drop to the normal form instead of a cryptic error.
        setSignedIn(false);
      } else {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Clear the session (the jwt is httpOnly, so only the server can drop it) WITHOUT leaving the
  // invite, then show the form again so the person it was mailed to can accept it on this
  // device. Mirrors ClaimTicket's logOutAndClaim.
  const logOutAndAccept = async () => {
    try {
      await fetch('/api/logout', { method: 'POST', credentials: 'include' });
    } catch { /* best-effort — the retry submits without the cookie regardless */ }
    localStorage.removeItem('isLoggedIn');
    window.dispatchEvent(new Event('auth-changed'));
    setWrongAccount(null);
    setSignedIn(false);
  };

  const valid = info && info.valid;

  return (
    <AuthShell
      title={valid ? info.company_name : t('adminInviteTitle')}
      subtitle={valid ? t(isAdminInvite ? 'adminInviteSubtitle' : 'memberInviteSubtitle') : ''}
      hideBrand={!!(info && info.logo_url)}
    >
      {info && info.logo_url && (
        <div className="text-center mb-3">
          <img src={info.logo_url} alt={info.community_name || ''}
               style={{ maxHeight: '56px', maxWidth: '180px', objectFit: 'contain' }} />
        </div>
      )}
      {loading ? (
        <div className="auth__card"><span className="auth__spinner" /></div>
      ) : !valid ? (
        <div className="auth__card">
          <p>{t('invalidLink')}</p>
        </div>
      ) : joined ? (
        <div className="auth__card">
          <p className="auth__hint" style={{ fontWeight: 600 }}>
            <Icon name="check" /> {t('memberInviteJoined', { company: info.company_name })}
          </p>
          <p className="auth__hint">{t('memberInviteJoinedNext', { brand })}</p>
        </div>
      ) : loginFirst ? (
        <div className="auth__card">
          <p className="auth__hint">
            <Icon name="user-check" /> {t('adminInviteLoginFirst')}
          </p>
          <button
            type="button"
            className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
            onClick={() => {
              // Login.js navigates to sessionStorage 'redirect' once signed in — park this invite
              // there so they land back here instead of hunting the link down in WhatsApp again.
              sessionStorage.setItem('redirect', `/admin-invite/${token}`);
              navigate('/login');
            }}
          >
            <Icon name="sign-in-alt" /> {t('login')}
          </button>
        </div>
      ) : wrongAccount !== null ? (
        /* The invite was mailed to one person and this browser is signed in as another — the
           admin who sent it, a shared laptop. Accepting here would put THAT account on the
           roster and leave the invited colleague pending, so the session goes first. */
        <div className="auth__card">
          <p className="auth__hint">
            <Icon name="user-check" />{' '}
            {wrongAccount
              ? t('inviteWrongAccountAs', { name: wrongAccount, email: boundEmail })
              : t('inviteWrongAccount', { email: boundEmail })}
          </p>
          <button
            type="button"
            className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
            onClick={logOutAndAccept}
          >
            <Icon name="sign-out-alt" /> {t('logOutAndAccept')}
          </button>
        </div>
      ) : signedIn ? (
        /* Already signed in (either straight from the link, or bounced back from the login above):
           the accept endpoint grants onto THAT session and ignores any typed details, so asking
           for them again would be a lie. One button. */
        <div className="auth__card">
          <p className="auth__hint" style={{ fontWeight: 600 }}>
            <Icon name="key" />{' '}
            {t(isAdminInvite ? 'adminInviteSignedIn' : k('memberInviteSignedIn'),
               { company: info.company_name, brand })}
          </p>
          {/* Say who it was mailed to, so a shared device notices before it clicks. */}
          {boundEmail && (
            <p className="auth__hint">{t('inviteIsFor', { email: boundEmail })}</p>
          )}
          <button
            type="button"
            className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
            onClick={accept}
            disabled={submitting}
          >
            {submitting ? <span className="auth__spinner" /> : <Icon name="user-add" />}
            {t(isAdminInvite ? 'acceptAdminInvite' : 'acceptMemberInvite')}
          </button>
          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </div>
      ) : (
        <form className="auth__card" onSubmit={accept}>
          <p className="auth__hint" style={{ fontWeight: 600 }}>
            <Icon name="key" />{' '}
            {info.inviter_name
              ? t(isAdminInvite ? 'adminInvitedBy' : k('memberInvitedBy'),
                  { name: info.inviter_name, company: info.company_name, brand })
              : t(isAdminInvite ? 'adminInvitedByCompany' : k('memberInvitedByCompany'),
                  { company: info.company_name, brand })}
          </p>
          <p className="auth__hint">
            {t(k(isAdminInvite ? 'adminInviteWhat' : 'memberInviteWhat'))}
          </p>

          <div className="auth__row">
            <div className="auth__field">
              <label className="auth__label">{t('firstName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('enterFirstName')}
                  autoComplete="given-name"
                  required
                />
              </div>
            </div>
            <div className="auth__field">
              <label className="auth__label">{t('lastName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('enterLastName')}
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>
          </div>

          <div className="auth__field">
            <label className="auth__label">
              {t('email')}
              {boundEmail && <span className="auth__hint"> <Icon name="lock" /></span>}
            </label>
            <div className="auth__input-wrap">
              <input
                className="auth__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('enterEmail')}
                autoComplete="email"
                readOnly={!!boundEmail}
                aria-readonly={!!boundEmail}
                required
              />
            </div>
            {/* Say WHY it is fixed, so a locked field reads as deliberate rather than broken. */}
            {boundEmail && (
              <p className="auth__hint">{t('inviteIsFor', { email: boundEmail })}</p>
            )}
          </div>

          <div className="auth__field">
            <label className="auth__label">
              {t('mobileNumber')}
              {!isAdminInvite && <span className="auth__hint"> ({t('optional')})</span>}
            </label>
            <div className="auth__input-wrap">
              <input
                className="auth__input"
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder={t('enterMobile')}
                autoComplete="tel"
                required={isAdminInvite}
              />
            </div>
            <WhatsAppNotice />
          </div>

          <button
            type="submit"
            className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
            disabled={submitting}
          >
            {submitting ? <span className="auth__spinner" /> : <Icon name="user-add" />}
            {t(isAdminInvite ? 'acceptAdminInvite' : 'acceptMemberInvite')}
          </button>

          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </form>
      )}

      {/* Brand explanation badge (learn more about Bravos). */}
      {valid && info.commercial_url && (
        <EventCta eventName={info.community_name} commercialUrl={info.commercial_url} flush />
      )}
    </AuthShell>
  );
};

export default AdminInviteAccept;
